'use client'
import React, { useMemo, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import Header from '../topbar'
import { CollapsibleSidebar, Sidebar, routerConfig } from '@/fe-common'
import type { ISideMenu } from '@/fe-common'
import './style.css'
import {
  hasPermission,
  usePermissionContext,
} from '@/context/permission-context'
import { useResourceContext } from '@/context/resource-context'
import { useCurrentProject } from '@/fe-common/hooks/user'

type PageLayoutProps = {
  children: React.ReactNode
}

const extraPathMap = {
  '/llm/apps': ['/llm/app/'],
  '/llm/explore/apps': ['/llm/explore/chat', '/llm/explore/installed'],
}

/** Next 实际路径（/ecs、/maas、/bill…）与 routes 中 /llm 前缀对齐，便于匹配菜单树 */
function toConfigMatchPath(pathname: string): string {
  if (pathname.startsWith('/iaas') || pathname.startsWith('/llm')) return pathname
  return `/llm${pathname}`
}

const HeaderWrapper = (props: PageLayoutProps) => {
  const { children } = props
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false)
  const pathname = usePathname()
  const basePath = process.env.NEXT_PUBLIC_BASE_URL || ''
  const router = useRouter()
  const { permissions, isAdminMode, isDomainMode } = usePermissionContext()
  const scopedResources = useResourceContext()
  const { userInfo } = useCurrentProject()

  function getRootRouterMenu(
    pathname: string,
    config: Record<string, ISideMenu[]>,
  ): ISideMenu | null {
    const walk = (r: ISideMenu): boolean => {
      if (!r.children || r.children.length === 0) {
        return (
          pathname.startsWith(r.path) ||
          (extraPathMap[r.path] &&
            extraPathMap[r.path].some(item => pathname.startsWith(item)))
        )
      }
      const exist = r.children.some(item => pathname.startsWith(item.path))
      if (exist) return true
      return r.children.map(i => walk(i)).some(i => i)
    }

    for (const key in config) {
      for (const item of config[key]) {
        if (walk(item)) return item
      }
    }
    return null
  }

  function filterConfig(
    config: Record<string, ISideMenu[]>,
    pathname: string,
  ): Record<string, ISideMenu[]> {
    const result: Record<string, ISideMenu[]> = {}
    for (const key in config) {
      const menu = filterMenus(config[key], pathname)
      if (menu.length > 0) result[key] = menu
    }
    return result
  }

  function filterMenus(menus: ISideMenu[], pathname: string): ISideMenu[] {
    if (!menus) return []
    return menus.reduce((acc, item) => {
      if (item.children && !item.hidden) {
        const children = filterMenus(item.children, pathname)
        if (children.length > 0) acc.push({ ...item, children })
      } else if (!item.hidden) {
        if (item.roles) {
          if (
            (userInfo?.roles?.filter(role => item.roles?.includes(role))
              .length ?? 0) > 0
          ) {
            acc.push(item)
          }
        } else if (
          hasPermission(
            item.permission,
            item.adminOnly,
            permissions,
            isAdminMode,
            isDomainMode,
            scopedResources,
          )
        )
          acc.push(item)
      }
      return acc
    }, [] as ISideMenu[])
  }

  // FIXME: remove prefix
  const menuItem = getRootRouterMenu(toConfigMatchPath(pathname), routerConfig)
  // TODO: add to local storage
  const [sidebarOpened, setSidebarOpened] = useState<boolean>(true)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const collapsedSidebarWidth = useMemo(() => {
    if (!sidebarOpened) return '0px'

    if (sidebarCollapsed) return '50px'

    return '160px'
  }, [sidebarCollapsed, sidebarOpened])

  const handleItemClick = (item: ISideMenu, domEvent: any) => {
    // window.location.href = window.location.origin + item.path
    if (item && item.path) {
      setSidebarVisible(false)
      const url = window.location.origin + item.path
      if (domEvent.ctrlKey || domEvent.metaKey || domEvent.button === 1) {
        window.open(url, '_blank')
      } else {
        if (item.path?.startsWith(basePath))
          router.push(item.path.substring(basePath?.length))
        else {
          window.location.href = url
        }
      }
    }
  }

  return (
    <>
      <div>
        <Header onMenuClicked={() => setSidebarVisible(p => !p)} />
      </div>
      <div className='flex h-full overflow-auto children-wrapper'>
        <CollapsibleSidebar
          title={menuItem?.msg ?? menuItem?.title}
          menus={filterMenus(menuItem?.children, pathname)}
          open={sidebarOpened}
          onVisibleChanged={v => setSidebarOpened(v)}
          collapsed={sidebarCollapsed}
          onCollapsed={c => setSidebarCollapsed(c)}
          getContainer={false}
          onItemClick={handleItemClick}
        />
        <div
          className='flex flex-col h-full overflow-auto'
          style={{
            width: `calc(100vw - ${collapsedSidebarWidth})`,
            height: '100%',
            marginLeft: collapsedSidebarWidth,
          }}>
          {children}
        </div>
      </div>
      <Sidebar
        menus={filterConfig(routerConfig, pathname)}
        open={sidebarVisible}
        onItemClick={handleItemClick}
        onClose={() => setSidebarVisible(false)}
      />
    </>
  )
}

export default React.memo(HeaderWrapper)
